import type {
  AccessPolicy,
  AcademicRecord,
  BadgeDefinition,
  InstitutionDomain,
  InstitutionIntegration,
  VerifiableInstitution,
  VerificationCase,
} from '../types/verification'

/**
 * Seeded development data. None of these figures are production metrics, and
 * no record here represents a real person or a real institutional confirmation.
 */

export const verifiableInstitutions: VerifiableInstitution[] = [
  {
    id: 'inst-unilag',
    name: 'University of Lagos',
    shortName: 'UNILAG',
    country: 'Nigeria',
    campuses: [
      { id: 'cmp-akoka', name: 'Akoka Main Campus', city: 'Lagos' },
      { id: 'cmp-idiaraba', name: 'Idi-Araba Campus', city: 'Lagos' },
    ],
    structure: {
      divisionLabel: 'Faculty',
      levelLabel: 'Level',
      levels: ['100 Level', '200 Level', '300 Level', '400 Level', '500 Level'],
      identifierLabel: 'Matriculation number',
      statuses: [
        'applicant',
        'admitted',
        'enrolled',
        'active_student',
        'leave_of_absence',
        'graduating',
        'graduated',
        'alumni',
      ],
    },
    maxLevelSupported: 'institution_integrated',
    expiryMonths: {
      institutional_email: 12,
      document: 12,
      institution_review: 12,
      integration: 12,
    },
    acceptedDocuments: [
      'Student identity card',
      'Course registration printout',
      'Admission letter',
      'Enrolment confirmation letter',
    ],
  },
  {
    id: 'inst-futa',
    name: 'Federal University of Technology, Akure',
    shortName: 'FUTA',
    country: 'Nigeria',
    campuses: [{ id: 'cmp-futa-main', name: 'Main Campus', city: 'Akure' }],
    structure: {
      divisionLabel: 'School',
      levelLabel: 'Level',
      levels: ['100 Level', '200 Level', '300 Level', '400 Level', '500 Level'],
      identifierLabel: 'Matriculation number',
      statuses: ['applicant', 'admitted', 'enrolled', 'active_student', 'graduating', 'graduated', 'alumni'],
    },
    // No institutional connection yet, so this institution genuinely cannot
    // reach the top level. The interface must not offer what it cannot do.
    maxLevelSupported: 'institution_verified',
    expiryMonths: { institutional_email: 12, document: 6, institution_review: 12 },
    acceptedDocuments: ['Student identity card', 'Course registration printout'],
  },
  {
    id: 'inst-kth',
    name: 'KTH Royal Institute of Technology',
    shortName: 'KTH',
    country: 'Sweden',
    campuses: [{ id: 'cmp-kth-main', name: 'Valhallavägen', city: 'Stockholm' }],
    structure: {
      // A different education system: no levels, different labels entirely.
      divisionLabel: 'School',
      levelLabel: null,
      levels: [],
      identifierLabel: 'Personal study identifier',
      statuses: ['applicant', 'admitted', 'enrolled', 'active_student', 'graduated', 'alumni'],
    },
    maxLevelSupported: 'email_verified',
    expiryMonths: { institutional_email: 12 },
    acceptedDocuments: ['Registration certificate'],
  },
]

export const institutionDomains: InstitutionDomain[] = [
  {
    id: 'dom-1',
    institutionId: 'inst-unilag',
    domain: 'unilag.edu.ng',
    state: 'approved',
    evidence: 'Confirmed in writing by the ICT directorate, plus DNS record check',
    addedBy: '@mary.o',
    addedAt: '2025-11-20T10:00:00Z',
    reviewedBy: '@david.a',
    note: 'Primary staff and student domain.',
  },
  {
    id: 'dom-2',
    institutionId: 'inst-unilag',
    domain: 'live.unilag.edu.ng',
    state: 'approved',
    evidence: 'Same institutional agreement; sub-domain confirmed by ICT directorate',
    addedBy: '@mary.o',
    addedAt: '2025-11-20T10:04:00Z',
    reviewedBy: '@david.a',
    note: 'Student mail domain issued from 2023 onward.',
  },
  {
    id: 'dom-3',
    institutionId: 'inst-futa',
    domain: 'futa.edu.ng',
    state: 'approved',
    evidence: 'Institutional agreement signed 4 Feb 2026',
    addedBy: '@mary.o',
    addedAt: '2026-02-06T09:00:00Z',
    reviewedBy: '@david.a',
    note: '',
  },
  {
    id: 'dom-4',
    institutionId: 'inst-futa',
    domain: 'student.futa.ng',
    state: 'pending_review',
    evidence: 'Submitted by a departmental officer. Ownership not yet established.',
    addedBy: '@adaeze.n',
    addedAt: '2026-08-10T15:20:00Z',
    reviewedBy: null,
    note: 'Not close enough to the confirmed domain to accept on resemblance. Blocked until ICT confirms.',
  },
  {
    id: 'dom-5',
    institutionId: 'inst-unilag',
    domain: 'unilag-students.com',
    state: 'rejected',
    evidence: 'Registered privately in 2026. Not an institutional domain.',
    addedBy: '@unknown',
    addedAt: '2026-07-02T12:00:00Z',
    reviewedBy: '@david.a',
    note: 'Rejected and recorded. A domain resembling an institution is not an institutional domain.',
  },
]

export const institutionIntegrations: InstitutionIntegration[] = [
  {
    institutionId: 'inst-unilag',
    model: 'api',
    state: 'live',
    provider: 'UNILAG student records API',
    canConfirm: [
      'Whether an identifier exists on the register',
      'Programme of study',
      'Level',
      'Current student status',
      'Campus',
    ],
    cannotConfirm: [
      'Grades or academic performance',
      'Disciplinary records',
      'Financial standing with the institution',
    ],
    credentialState: 'held',
    credentialRotatedAt: '2026-06-01T00:00:00Z',
    lastSuccessAt: '2026-08-14T06:12:00Z',
    lastFailureAt: '2026-08-02T23:40:00Z',
    note: 'Read-only. Campuzon queries; it does not write to the institutional record.',
  },
  {
    institutionId: 'inst-futa',
    model: 'portal',
    state: 'configured',
    provider: 'Institution review portal',
    canConfirm: ['Affiliation confirmed by a named institutional officer'],
    cannotConfirm: ['Anything automatically — every confirmation is a human decision'],
    credentialState: 'held',
    credentialRotatedAt: '2026-02-06T00:00:00Z',
    lastSuccessAt: '2026-08-13T11:05:00Z',
    lastFailureAt: null,
    note: 'Officers review requests in their own console. There is no automated feed.',
  },
  {
    institutionId: 'inst-kth',
    model: 'none',
    state: 'none',
    provider: '—',
    canConfirm: [],
    cannotConfirm: ['Everything — there is no agreement or connection in place'],
    credentialState: 'absent',
    credentialRotatedAt: null,
    lastSuccessAt: null,
    lastFailureAt: null,
    note: 'Institutional email is the only method available here, and it is labelled as exactly that.',
  },
]

/* ------------------------------------------------- the account on this device */

export const myAcademicRecords: AcademicRecord[] = [
  {
    id: 'acr-1',
    userId: 'CAMP-2AB19XQ7',
    kind: 'student',
    institutionId: 'inst-futa',
    campusId: 'cmp-futa-main',
    division: { value: 'School of Engineering and Engineering Technology', source: 'declared', confirmedAt: null },
    department: { value: 'Mechanical Engineering', source: 'institution', confirmedAt: '2026-03-02T10:00:00Z' },
    programme: { value: 'B.Eng. Mechanical Engineering', source: 'institution', confirmedAt: '2026-03-02T10:00:00Z' },
    level: { value: '300 Level', source: 'institution', confirmedAt: '2026-03-02T10:00:00Z' },
    studentIdentifier: 'MEE/19/4021',
    academicSession: '2025/2026',
    status: 'active_student',
    statusSource: 'institution',
    verification: {
      level: 'institution_verified',
      state: 'verified',
      method: 'institution_review',
      source: 'Federal University of Technology, Akure — Student Affairs',
      meaning:
        'An authorised representative of the institution confirmed this affiliation directly.',
      verifiedAt: '2026-03-02T10:00:00Z',
      expiresAt: '2027-03-02T10:00:00Z',
      lastRevalidatedAt: '2026-03-02T10:00:00Z',
      caseId: 'VER-2026-000841',
      emailMasked: 's•••••••@futa.edu.ng',
    },
    visibility: {
      institution: 'campus',
      programme: 'campus',
      level: 'campus',
      status: 'campus',
      identifier: 'private',
    },
    history: [
      {
        id: 'hst-1',
        at: '2026-01-14T08:30:00Z',
        field: 'Academic record',
        before: null,
        after: 'Declared: B.Eng. Mechanical Engineering, 300 Level',
        source: 'declared',
        changedBy: '@saviour',
        reason: 'Entered during onboarding',
        verificationAtTime: 'declared',
      },
      {
        id: 'hst-2',
        at: '2026-01-14T09:05:00Z',
        field: 'Verification level',
        before: 'unverified',
        after: 'email_verified',
        source: 'system',
        changedBy: 'Verification engine',
        reason: 'Access to s•••••••@futa.edu.ng confirmed by single-use code',
        verificationAtTime: 'verified',
      },
      {
        id: 'hst-3',
        at: '2026-03-02T10:00:00Z',
        field: 'Verification level',
        before: 'email_verified',
        after: 'institution_verified',
        source: 'institution',
        changedBy: '@futa.registry',
        reason: 'Affiliation confirmed against the institutional register',
        verificationAtTime: 'verified',
      },
      {
        id: 'hst-4',
        at: '2026-03-02T10:00:00Z',
        field: 'Programme',
        before: 'B.Eng. Mechanical Engineering (declared)',
        after: 'B.Eng. Mechanical Engineering (institution)',
        source: 'institution',
        changedBy: '@futa.registry',
        reason: 'Declared value matched the institutional record and was promoted to institution-sourced',
        verificationAtTime: 'verified',
      },
    ],
    createdAt: '2026-01-14T08:30:00Z',
    updatedAt: '2026-03-02T10:00:00Z',
  },
  {
    // A second, legitimate affiliation. One person, two institutions.
    id: 'acr-2',
    userId: 'CAMP-2AB19XQ7',
    kind: 'applicant',
    institutionId: 'inst-kth',
    campusId: 'cmp-kth-main',
    division: { value: 'School of Industrial Engineering and Management', source: 'declared', confirmedAt: null },
    department: { value: 'Energy Technology', source: 'declared', confirmedAt: null },
    programme: { value: 'M.Sc. Sustainable Energy Engineering', source: 'declared', confirmedAt: null },
    level: null,
    studentIdentifier: null,
    academicSession: '2027 intake',
    status: 'applicant',
    statusSource: 'declared',
    verification: {
      level: 'unverified',
      state: 'declared',
      method: null,
      source: null,
      meaning: 'Everything here was entered by you and has not been checked by anyone.',
      verifiedAt: null,
      expiresAt: null,
      lastRevalidatedAt: null,
      caseId: null,
      emailMasked: null,
    },
    visibility: {
      institution: 'private',
      programme: 'private',
      level: 'private',
      status: 'private',
      identifier: 'private',
    },
    history: [
      {
        id: 'hst-5',
        at: '2026-07-30T19:10:00Z',
        field: 'Academic record',
        before: null,
        after: 'Declared: M.Sc. Sustainable Energy Engineering, applicant',
        source: 'declared',
        changedBy: '@saviour',
        reason: 'Added while comparing programmes in Discover',
        verificationAtTime: 'declared',
      },
    ],
    createdAt: '2026-07-30T19:10:00Z',
    updatedAt: '2026-07-30T19:10:00Z',
  },
]

/* ------------------------------------------------------------------- cases */

export const verificationCases: VerificationCase[] = [
  {
    id: 'VER-2026-001284',
    userId: 'CAMP-8HJ22PLM',
    handle: 'amarachi',
    displayName: 'Amarachi Nwosu',
    institutionId: 'inst-unilag',
    campusId: 'cmp-akoka',
    method: 'institution_review',
    requestedLevel: 'institution_verified',
    claimed: { programme: 'B.Sc. Systems Engineering', level: '300 Level', status: 'active_student' },
    state: 'under_review',
    priority: 'normal',
    submittedAt: '2026-08-12T09:15:00Z',
    assignedTo: '@unilag.registry',
    evidence: [
      {
        id: 'ev-1',
        kind: 'Student identity card',
        filename: 'unilag-id-front.jpg',
        reference: 'doc://secure/8f21c4',
        submittedAt: '2026-08-12T09:15:00Z',
        restricted: true,
      },
      {
        id: 'ev-2',
        kind: 'Course registration printout',
        filename: 'registration-2025-2026.pdf',
        reference: 'doc://secure/9a03de',
        submittedAt: '2026-08-12T09:16:00Z',
        restricted: true,
      },
    ],
    decision: { outcome: null, by: null, at: null, reason: null },
    internalNotes: ['Identity card image is legible. Awaiting register check on the identifier.'],
    events: [
      {
        id: 'cev-1',
        at: '2026-08-12T09:15:00Z',
        actor: '@amarachi',
        action: 'Case opened',
        detail: 'Requested institution verification and attached two documents.',
      },
      {
        id: 'cev-2',
        at: '2026-08-12T11:40:00Z',
        actor: '@unilag.registry',
        action: 'Assigned',
        detail: 'Taken by the Akoka registry team.',
      },
      {
        id: 'cev-3',
        at: '2026-08-13T08:05:00Z',
        actor: '@unilag.registry',
        action: 'Moved to under review',
        detail: 'Checking the identifier against the 2025/2026 register.',
      },
    ],
    appealOfCaseId: null,
    riskSignals: [],
    conflict: null,
  },
  {
    id: 'VER-2026-001291',
    userId: 'CAMP-3RT88KDS',
    handle: 'kelechi.i',
    displayName: 'Kelechi Ibe',
    institutionId: 'inst-unilag',
    campusId: 'cmp-akoka',
    method: 'document',
    requestedLevel: 'document_verified',
    claimed: { programme: 'B.Sc. Accounting', level: '200 Level', status: 'active_student' },
    state: 'information_requested',
    priority: 'normal',
    submittedAt: '2026-08-11T14:02:00Z',
    assignedTo: '@david.a',
    evidence: [
      {
        id: 'ev-3',
        kind: 'Student identity card',
        filename: 'id-photo.jpg',
        reference: 'doc://secure/1c77aa',
        submittedAt: '2026-08-11T14:02:00Z',
        restricted: true,
      },
    ],
    decision: { outcome: null, by: null, at: null, reason: null },
    internalNotes: ['Card has no visible session year. Asked for a registration printout.'],
    events: [
      {
        id: 'cev-4',
        at: '2026-08-11T14:02:00Z',
        actor: '@kelechi.i',
        action: 'Case opened',
        detail: 'Requested document verification.',
      },
      {
        id: 'cev-5',
        at: '2026-08-12T10:15:00Z',
        actor: '@david.a',
        action: 'Information requested',
        detail:
          'The card alone does not establish current enrolment. Asked for a registration printout for 2025/2026.',
      },
    ],
    appealOfCaseId: null,
    riskSignals: [],
    conflict: null,
  },
  {
    id: 'VER-2026-001302',
    userId: 'CAMP-5MN01WQZ',
    handle: 'chidera.o',
    displayName: 'Chidera Okonkwo',
    institutionId: 'inst-futa',
    campusId: 'cmp-futa-main',
    method: 'institution_review',
    requestedLevel: 'institution_verified',
    claimed: { programme: 'B.Tech. Industrial Design', level: '200 Level', status: 'active_student' },
    state: 'under_review',
    priority: 'high',
    submittedAt: '2026-08-13T07:45:00Z',
    assignedTo: null,
    evidence: [
      {
        id: 'ev-4',
        kind: 'Course registration printout',
        filename: 'reg-printout.pdf',
        reference: 'doc://secure/44b901',
        submittedAt: '2026-08-13T07:45:00Z',
        restricted: true,
      },
    ],
    decision: { outcome: null, by: null, at: null, reason: null },
    internalNotes: [],
    events: [
      {
        id: 'cev-6',
        at: '2026-08-13T07:45:00Z',
        actor: '@chidera.o',
        action: 'Case opened',
        detail: 'Requested institution verification.',
      },
      {
        id: 'cev-7',
        at: '2026-08-13T07:46:00Z',
        actor: 'Risk engine',
        action: 'Flagged for review',
        detail: 'The identifier on the printout is already held by another account.',
      },
    ],
    appealOfCaseId: null,
    riskSignals: [
      'This identifier is already held by one other account at this institution',
      'Both accounts were created within the same week',
    ],
    conflict: {
      field: 'Programme',
      declared: 'B.Tech. Industrial Design',
      institution: 'B.Tech. Industrial Mathematics',
    },
  },
  {
    id: 'VER-2026-001199',
    userId: 'CAMP-9PL34XCV',
    handle: 'tunde.b',
    displayName: 'Tunde Bakare',
    institutionId: 'inst-futa',
    campusId: 'cmp-futa-main',
    method: 'document',
    requestedLevel: 'document_verified',
    claimed: { programme: 'B.Sc. Computer Science', level: '300 Level', status: 'active_student' },
    state: 'rejected',
    priority: 'normal',
    submittedAt: '2026-07-28T16:20:00Z',
    assignedTo: '@david.a',
    evidence: [
      {
        id: 'ev-5',
        kind: 'Student identity card',
        filename: 'id-scan.png',
        reference: 'doc://secure/77ce12',
        submittedAt: '2026-07-28T16:20:00Z',
        restricted: true,
      },
    ],
    decision: {
      outcome: 'rejected',
      by: '@david.a',
      at: '2026-07-30T09:10:00Z',
      reason:
        'The document could not be read clearly enough to confirm the name and session. This is not a finding about you — resubmit a clearer copy or ask the institution to confirm directly.',
    },
    internalNotes: ['Image heavily compressed. No suggestion of tampering.'],
    events: [
      {
        id: 'cev-8',
        at: '2026-07-28T16:20:00Z',
        actor: '@tunde.b',
        action: 'Case opened',
        detail: 'Requested document verification.',
      },
      {
        id: 'cev-9',
        at: '2026-07-30T09:10:00Z',
        actor: '@david.a',
        action: 'Rejected',
        detail: 'Document illegible. Reason given to the student, with both remedies named.',
      },
    ],
    appealOfCaseId: null,
    riskSignals: [],
    conflict: null,
  },
  {
    id: 'VER-2026-001310',
    userId: 'CAMP-9PL34XCV',
    handle: 'tunde.b',
    displayName: 'Tunde Bakare',
    institutionId: 'inst-futa',
    campusId: 'cmp-futa-main',
    method: 'document',
    requestedLevel: 'document_verified',
    claimed: { programme: 'B.Sc. Computer Science', level: '300 Level', status: 'active_student' },
    state: 'submitted',
    priority: 'normal',
    submittedAt: '2026-08-14T07:00:00Z',
    assignedTo: null,
    evidence: [
      {
        id: 'ev-6',
        kind: 'Course registration printout',
        filename: 'registration-clear.pdf',
        reference: 'doc://secure/aa5501',
        submittedAt: '2026-08-14T07:00:00Z',
        restricted: true,
      },
    ],
    decision: { outcome: null, by: null, at: null, reason: null },
    internalNotes: [],
    events: [
      {
        id: 'cev-10',
        at: '2026-08-14T07:00:00Z',
        actor: '@tunde.b',
        action: 'Appeal opened',
        detail:
          'Appeal of VER-2026-001199 with a clearer document. The original decision remains on record.',
      },
    ],
    appealOfCaseId: 'VER-2026-001199',
    riskSignals: [],
    conflict: null,
  },
]

/* ---------------------------------------------------------------- policies */

export const accessPolicies: AccessPolicy[] = [
  {
    id: 'pol-1',
    feature: 'FUTA Students community',
    surface: 'Social',
    requirement: 'Current student at FUTA, confirmed by the institution',
    requiredLevel: 'institution_verified',
    requiredStatuses: ['enrolled', 'active_student', 'graduating'],
    requiredInstitutionId: 'inst-futa',
    fallback: 'You can still read the campus feed and join open communities.',
  },
  {
    id: 'pol-2',
    feature: 'Mechanical Engineering 300 Level group',
    surface: 'Social',
    requirement: 'Verified programme and level',
    requiredLevel: 'institution_verified',
    requiredStatuses: ['enrolled', 'active_student'],
    requiredInstitutionId: 'inst-futa',
    fallback: 'Open study groups for the same course have no verification requirement.',
  },
  {
    id: 'pol-3',
    feature: 'Roommate matching',
    surface: 'Housing',
    requirement: 'Academic identity verified to at least document level',
    requiredLevel: 'document_verified',
    requiredStatuses: null,
    requiredInstitutionId: null,
    fallback: 'You can browse housing listings and apply without roommate matching.',
  },
  {
    id: 'pol-4',
    feature: 'Selling in the campus marketplace',
    surface: 'Marketplace',
    requirement: 'Institutional email verified',
    requiredLevel: 'email_verified',
    requiredStatuses: null,
    requiredInstitutionId: null,
    fallback: 'You can buy without this. Selling needs an institutional email on file.',
  },
  {
    id: 'pol-5',
    feature: 'Browsing the marketplace',
    surface: 'Marketplace',
    requirement: 'A Campuzon account',
    requiredLevel: 'unverified',
    requiredStatuses: null,
    requiredInstitutionId: null,
    fallback: null,
  },
  {
    id: 'pol-6',
    feature: 'Student-priced knowledge licences',
    surface: 'Marketplace',
    requirement: 'Current student confirmed by the institution',
    requiredLevel: 'institution_verified',
    requiredStatuses: ['enrolled', 'active_student', 'graduating'],
    requiredInstitutionId: null,
    fallback: 'Standard pricing applies without it.',
  },
  {
    id: 'pol-7',
    feature: 'Alumni network',
    surface: 'Community',
    requirement: 'Graduated or alumni status, verification preserved',
    requiredLevel: 'institution_verified',
    requiredStatuses: ['graduated', 'alumni'],
    requiredInstitutionId: null,
    fallback: 'Current students join the student communities instead.',
  },
]

/* ------------------------------------------------------------------ badges */

export const badgeDefinitions: BadgeDefinition[] = [
  {
    id: 'bdg-1',
    label: 'Verified student',
    asserts: 'The institution confirmed this person is currently enrolled on the stated programme.',
    doesNotAssert: 'Anything about conduct, safety, or how they behave in a transaction.',
    active: true,
  },
  {
    id: 'bdg-2',
    label: 'Institutional email verified',
    asserts: 'Access to an approved institutional email address was confirmed.',
    doesNotAssert: 'Current enrolment, programme, or level.',
    active: true,
  },
  {
    id: 'bdg-3',
    label: 'Academic identity verified',
    asserts: 'Campuzon reviewed and accepted academic documentation.',
    doesNotAssert: 'That the institution itself confirmed anything.',
    active: true,
  },
  {
    id: 'bdg-4',
    label: 'Verified lecturer',
    asserts: 'The institution confirmed this person holds a teaching role.',
    doesNotAssert: 'Which courses they teach, or any authority over your record.',
    active: true,
  },
  {
    id: 'bdg-5',
    label: 'Alumni',
    asserts: 'A previously verified affiliation that has completed.',
    doesNotAssert: 'Current enrolment or current institutional standing.',
    active: true,
  },
  {
    id: 'bdg-6',
    label: 'Verified',
    asserts: '—',
    doesNotAssert: '—',
    active: false,
  },
]

/* --------------------------------------------------------------- analytics */

export const verificationMetrics = [
  { id: 'vm-1', label: 'Declared academic identities', value: '1,842', basis: 'Records with a declared institution' },
  { id: 'vm-2', label: 'Institutional email verified', value: '946', basis: 'Level 1 and above' },
  { id: 'vm-3', label: 'Document verified', value: '318', basis: 'Level 2 and above' },
  { id: 'vm-4', label: 'Institution verified', value: '204', basis: 'Level 3 and above' },
  { id: 'vm-5', label: 'Confirmed by integration', value: '61', basis: 'Level 4, UNILAG connection only' },
  { id: 'vm-6', label: 'Cases open', value: '37', basis: 'Submitted, under review or information requested' },
  { id: 'vm-7', label: 'Median time to decision', value: '2.4 days', basis: 'Case opened to decision recorded' },
  { id: 'vm-8', label: 'Rejected, last 30 days', value: '18', basis: 'Of which 11 were reopened as appeals' },
  { id: 'vm-9', label: 'Expiring within 30 days', value: '84', basis: 'Verified records approaching revalidation' },
  { id: 'vm-10', label: 'Duplicate identifier flags', value: '6', basis: 'Queued for human review, none auto-actioned' },
]
